import {Panel} from "@/instantsearch/components/Panel";
import {Label, Slider} from "radix-ui";
import {useState,} from "react";
import {useRange, type UseRangeProps} from "react-instantsearch";

type FilterRangeProps = UseRangeProps & {
    title: string;
    inputAddon?: string;
    labelApply: string;
    labelReset: string;
};


export function FilterRange({
                                inputAddon,
                                labelReset,
                                labelApply,
                                attribute,
                                precision,
                                ...props
                            }: FilterRangeProps) {
    const {start, range, canRefine, refine} = useRange({attribute, precision}, {
        $$widgetType: 'dc.rangeSlider'
    });
    const {min, max} = range;
    const identifierMin = attribute + "-min";
    const identifierMax = attribute + "-max";
    const step = 1 / Math.pow(10, precision || 0);
    const values = {
        min: Math.max(min as number, Number.isFinite(start[0]) ? (start[0] as number) : (min as number)),
        max: Math.min(max as number, Number.isFinite(start[1]) ? (start[1] as number) : (max as number))
    };
    const [prevValues, setPrevValues] = useState(values);

    const [{from, to}, setRange] = useState({
        from: values.min,
        to: values.max,
    });
    if(!canRefine) return;
    if (values.min !== prevValues.min || values.max !== prevValues.max) {
        setRange({from: values.min, to: values.max});
        setPrevValues(values);
    }


    return (
        <Panel title={props.title}>
            <div className={"ais-filterRange__root"}>
                <form className={"ais-filterRange__form"}
                      onSubmit={(event) => {
                          event.preventDefault();
                          refine([from ? Number(from) : undefined, to ? Number(to) : undefined]);
                      }}>
                    <div className={"ais-filterRange__controls"}>
                        <Label.Root className={"ais-filterRange__label"} htmlFor={identifierMin}>
                            <input
                                className={"ais-filterRange__input"}
                                type="number"
                                min={range.min}
                                max={range.max}
                                value={from}
                                step={step}
                                name={identifierMin}
                                id={identifierMin}
                                placeholder={range.min?.toString()}
                                disabled={!canRefine}
                                onInput={({currentTarget}) => {
                                    const value = currentTarget.value;
                                    setRange({from: parseFloat(value), to});
                                }}
                            />
                            {
                                inputAddon &&
                                <span aria-hidden={true} className={"ais-filterRange__inputAddon"}>{inputAddon}</span>
                            }
                        </Label.Root>
                        <span aria-hidden={true}>-</span>
                        <Label.Root className={"ais-filterRange__label"} htmlFor={identifierMax}>
                            <input
                                className={"ais-filterRange__input"}
                                type="number"
                                min={range.min}
                                max={range.max}
                                value={to}
                                step={step}
                                name={identifierMax}
                                id={identifierMax}
                                placeholder={range.max?.toString()}
                                disabled={!canRefine}
                                onInput={({currentTarget}) => {
                                    const value = currentTarget.value;
                                    setRange({from, to: parseFloat(value)});
                                }}
                            />
                            {
                                inputAddon &&
                                <span aria-hidden={true} className={"ais-filterRange__inputAddon"}>{inputAddon}</span>
                            }
                        </Label.Root>
                    </div>
                    <Slider.Root
                        min={range.min}
                        max={range.max}
                        value={[from as number, to as number]}
                        onValueChange={(value) => {
                            setRange({from: value[0], to: value[1]});
                        }}
                        onValueCommit={(value) => {
                            setRange({from: value[0], to: value[1]});
                        }}
                        step={step}
                        disabled={!canRefine}
                        aria-label={"Range"}
                        className={"ais-slider__root"}
                    >
                        <Slider.Track className={"ais-slider__track"}>
                            <Slider.Range className={"ais-slider__range"}/>
                        </Slider.Track>
                        <Slider.Thumb name={identifierMin + "-range"} className={"ais-slider__thumb"}/>
                        <Slider.Thumb name={identifierMax + "-range"} className={"ais-slider__thumb"}/>
                    </Slider.Root>
                    {
                        ((values.min+""+values.max) !== (from+""+to))&&
                        <div className={"ais-filterRange__actions"}>
                            <button type={"submit"}
                                    className={"ais-filterRange__button ais-filterRange__button--apply"}>{labelApply}</button>
                            <button className={"ais-filterRange__button linkWithIcon ais-filterRange__button--reset"}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        setRange({
                                            from: values.min,
                                            to: values.max,
                                        })
                                    }}>
                                <i className={"icon icon-retoure"} aria-hidden={true}></i>
                                <span> {labelReset}</span></button>
                        </div>
                    }
                </form>
            </div>
        </Panel>
    );
}
